/**
 *
 * This module is used to configure axios instance.
 *
 * Initially use baseURL option is used, Configure axios as required in future.
 * Reference https://github.com/axios/axios#config-defaults for more information.
 */
import axios, {AxiosError, AxiosInstance, AxiosResponse} from 'axios';
import {Config} from 'config';

type SessionChangeResponse = {
    error: string;
    redirectTo: string;
    sessionChanged: boolean;
    success: number;
};

type ApiResponse = {
    success?: number;
    suspended?: boolean;
    detail?: string;
    error?: string;
    messages?: string[];
};

/**
 * A hacky type we use to add fields to AxiosResponse. These fields make the
 * response object function directly as the `Error` objects previously thrown
 * while also supporting places where the caught errors are passed directly
 * into `genericMessageHandler()` where the messages are expected to be a list.
 */
type AxiosAdditionalErrorFields = {
    message?: string;
    messages?: string[];
};

// Is essentially (AxiosResponse<ApiResponse> & Error)
export type AxiosResponseOrError<T extends object = ApiResponse> =
    AxiosResponse<T> & AxiosAdditionalErrorFields;

// Is just declaring a type for func below in order to silence errors.
export type CustomAxiosError<T extends object = ApiResponse> = AxiosError<T> & {
    response: AxiosResponseOrError;
};

/**
 *
 *
 * @class Axios
 */
class Axios {
    static axiosInstance: AxiosInstance;

    static onLoggedOut: () => void;
    static onSuspendedUser: () => void;
    static onSessionChangedUser: (response: SessionChangeResponse) => void;

    /**
     * @static
     * @param {string} [url='']
     * @return {AxiosInstance}
     * @memberof Axios
     */
    static instance(url = ''): AxiosInstance {
        if (typeof Axios.axiosInstance === 'undefined') {
            Axios.axiosInstance = axios.create({
                baseURL: url != '' ? url : Config.api,
            });

            Axios.axiosInstance.interceptors.response.use(
                (response) => {
                    return response;
                },
                (error: CustomAxiosError) => {
                    if (
                        error.response &&
                        error.response.headers['x-co-authenticated'] == 'No' &&
                        Axios.onLoggedOut &&
                        typeof Axios.onLoggedOut === 'function'
                    ) {
                        Axios.onLoggedOut();
                    }

                    // if suspended user
                    if (
                        error.response &&
                        error.response.status === 403 &&
                        error.response.data.suspended &&
                        Axios.onSuspendedUser &&
                        typeof Axios.onSuspendedUser === 'function'
                    ) {
                        Axios.onSuspendedUser();
                    }

                    // if session_change user
                    if (
                        error.response &&
                        error.response.status === 401 &&
                        error.response.data.sessionChanged &&
                        Axios.onSessionChangedUser &&
                        typeof Axios.onSessionChangedUser === 'function'
                    ) {
                        Axios.onSessionChangedUser(error.response.data);
                    }

                    if (error?.response) {
                        if (error.response?.data) {
                            // Compat for places that just expect an `Error.message`
                            error.response.message =
                                error.response.data?.detail ??
                                error.response.data?.error;
                            error.response.messages = error.response.data
                                ?.messages ?? [error.response.message];

                            if (
                                typeof error.response.data?.exception ===
                                'object'
                            ) {
                                // eslint-disable-next-line no-console -- checked field only appears in non-prod envs
                                console.warn(
                                    'This error will not be logged in production.\n\n' +
                                        'If this request failure is unexpected, please include this information in ' +
                                        'your Jira ticket along with what you were doing prior to the failure:\n\n' +
                                        JSON.stringify(error.response.data)
                                );
                            }
                        }
                        return Promise.reject(error.response);
                    }

                    return Promise.reject(error);
                }
            );
        }

        return Axios.axiosInstance;
    }
}

export default Axios;
